import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import PaymentSummary from "@/components/PaymentSummary";
import QrButton from "@/components/QrButton";
import {
  Plus, Copy, ExternalLink, Loader2, RefreshCw, Ban, Wallet, Link as LinkIcon,
  CheckCircle2, Clock, XCircle, AlertCircle, Search, PauseCircle, PlayCircle, Power,
} from "lucide-react";

const STATUS_PILL = {
  pending:     { color: "border-amber-300 text-amber-700 bg-amber-50",       icon: Clock },
  active:      { color: "border-blue-300 text-blue-700 bg-blue-50",          icon: CheckCircle2 },
  paid:        { color: "border-emerald-300 text-emerald-700 bg-emerald-50", icon: CheckCircle2 },
  expired:     { color: "border-stone-300 text-stone-600 bg-stone-50",       icon: Clock },
  revoked:     { color: "border-rose-300 text-rose-700 bg-rose-50",          icon: Ban },
  inactive:    { color: "border-orange-300 text-orange-700 bg-orange-50",    icon: PauseCircle },
  deactivated: { color: "border-gray-400 text-gray-600 bg-gray-100",        icon: Ban },
  failed:      { color: "border-red-300 text-red-700 bg-red-50",            icon: XCircle },
};

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso.slice(0, 16).replace("T", " ");
    return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return iso.slice(0, 16).replace("T", " "); }
};

const payUrl = (link) => {
  const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
  return `${base}/pay/${link.id}`;
};

/** Compute the preview URL the backend will mint for a brand-new link.
 *  Mirrors `_build_checkout_url()` in backend/routes/admin_payment_links.py:
 *    - if partner_domain is configured → https://<partner_domain>/p/<id>
 *    - else → current origin/p/<id>
 *  Returns the URL with `<id>` as a placeholder. */
const buildPreviewUrl = (partnerDomain) => {
  if (partnerDomain) {
    const clean = String(partnerDomain).replace(/^https?:\/\//, "").replace(/\/+$/, "");
    return `https://${clean}/p/<id>`;
  }
  const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
  return `${base}/p/<id>`;
};

export default function AdminPaymentLinks() {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({
    type: "generic",
    partner_id: "",
    customer_id: "",
    amount: "",
    min_amount: "",
    max_amount: "",
    remarks: "",
    expires_in_hours: 24,
    gateway: "",
    pg: "",
    self_amount: false,
    suggested_amounts: "",
    max_attempts: "",
    max_successful_payments: "1",
    wallet_target: "partner",
    auto_credit: true,
  });
  const [creating, setCreating] = useState(false);
  const [gateways, setGateways] = useState([]);
  const [pgChannels, setPgChannels] = useState([]);
  const [pgChannelsLoading, setPgChannelsLoading] = useState(false);
  const [pgBlocked, setPgBlocked] = useState(false);  // Set when ManaRecharge returns the global limit error

  const [revokeOpen, setRevokeOpen] = useState(null); // link or null
  const [revokeReason, setRevokeReason] = useState("");
  const [refreshingId, setRefreshingId] = useState(null);
  const [partnerDomain, setPartnerDomain] = useState("");  // For URL preview in create dialog

  // Load partner_domain once for the preview-URL chip
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/domains");
        if (!cancelled) setPartnerDomain(data?.config?.partner_domain || "");
      } catch {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [masterBalance, setMasterBalance] = useState(null);

  const loadLinks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterType !== "all") params.type = filterType;
      const { data } = await api.get("/admin/payment-links", { params });
      setLinks(data.items || []);
      setTotals(data.totals || {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load links");
    } finally {
      setLoading(false);
    }
  };

  const loadMasterBalance = async () => {
    try {
      const { data } = await api.get("/admin/payment-links/master-wallet/balance");
      setMasterBalance(data.balance);
    } catch {
      // silent — only admins see this
    }
  };

  const loadPartners = async () => {
    try {
      const { data } = await api.get("/admin/users");
      const items = Array.isArray(data) ? data : (data.items || []);
      setPartners(items.filter((u) => u.role === "partner" || (u.role || "").startsWith("custom_partner_")));
    } catch {
      setPartners([]);
    }
  };

  useEffect(() => { loadLinks(); }, [filterStatus, filterType]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadPartners(); loadMasterBalance(); loadGateways(); loadPgChannels(); }, []);

  const loadGateways = async () => {
    try {
      const { data } = await api.get("/admin/payment-links/gateways");
      setGateways(data.gateways || []);
    } catch { /* silent */ }
  };

  const [pgRecommended, setPgRecommended] = useState(null); // { recommended, rate, paid, total }

  const loadPgChannels = async () => {
    setPgChannelsLoading(true);
    try {
      const { data } = await api.get("/admin/payment-links/pg-channels");
      setPgChannels(data.channels || []);
    } catch { setPgChannels([]); }
    finally { setPgChannelsLoading(false); }
  };

  const loadPgRecommended = async () => {
    try {
      const { data } = await api.get("/admin/payment-links/pg-channels/recommended");
      setPgRecommended(data?.recommended ? data : null);
    } catch { setPgRecommended(null); }
  };

  // Refresh recommendation each time the create dialog opens, and auto-fill
  // the PG channel field (only if the admin hasn't picked one yet).
  useEffect(() => {
    if (createOpen) {
      loadPgRecommended();
    }
  }, [createOpen]);

  useEffect(() => {
    if (createOpen && pgRecommended?.recommended && !form.pg && (form.gateway === "manarecharge" || !form.gateway)) {
      setForm((f) => ({ ...f, pg: pgRecommended.recommended }));
    }
  }, [createOpen, pgRecommended, form.gateway]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredLinks = useMemo(() => {
    if (!searchTerm.trim()) return links;
    const t = searchTerm.toLowerCase();
    return links.filter((l) =>
      (l.id || "").toLowerCase().includes(t) ||
      (l.remarks || "").toLowerCase().includes(t) ||
      (l.partner_snapshot?.full_name || "").toLowerCase().includes(t) ||
      (l.partner_snapshot?.mobile || "").includes(t) ||
      (l.provider_ref || "").toLowerCase().includes(t) ||
      (l.provider_order_id || "").toLowerCase().includes(t) ||
      (l.customer_mobile || "").includes(t)
    );
  }, [links, searchTerm]);

  const submitCreate = async () => {
    const amt = parseFloat(form.amount) || 0;
    if (!form.self_amount && (!amt || amt <= 0)) { toast.error("Amount required for fixed-amount links"); return; }
    if (form.type === "partner_topup" && !form.partner_id) { toast.error("Pick a partner"); return; }
    if (form.type === "wallet_topup" && form.wallet_target === "partner" && !form.partner_id) { toast.error("Pick a partner for wallet top-up"); return; }
    if (form.type === "wallet_topup" && form.wallet_target === "customer" && !form.customer_id) { toast.error("Enter customer ID for wallet top-up"); return; }
    setCreating(true);
    try {
      const payload = {
        type: form.type,
        amount: amt,
        remarks: form.remarks || null,
        expires_in_hours: parseInt(form.expires_in_hours || "24", 10),
        self_amount: form.self_amount,
        max_successful_payments: parseInt(form.max_successful_payments || "1", 10),
      };
      if (form.type === "partner_topup" || (form.type === "wallet_topup" && form.wallet_target === "partner")) payload.partner_id = form.partner_id;
      if (form.type === "wallet_topup") {
        payload.wallet_target = form.wallet_target;
        payload.auto_credit = form.auto_credit;
        if (form.wallet_target === "customer") payload.customer_id = form.customer_id;
      }
      if (form.min_amount) payload.min_amount = parseFloat(form.min_amount);
      if (form.max_amount) payload.max_amount = parseFloat(form.max_amount);
      if (form.gateway) payload.gateway = form.gateway;
      if ((!form.gateway || form.gateway === "manarecharge") && form.pg) payload.pg = form.pg;
      if (form.max_attempts) payload.max_attempts = parseInt(form.max_attempts, 10);
      if (form.suggested_amounts) {
        payload.suggested_amounts = form.suggested_amounts.split(",").map((s) => parseFloat(s.trim())).filter((n) => n > 0);
      }
      const { data } = await api.post("/admin/payment-links", payload);
      toast.success("Payment link created");
      setCreateOpen(false);
      setForm({ type: "generic", partner_id: "", customer_id: "", amount: "", min_amount: "", max_amount: "", remarks: "", expires_in_hours: 24, gateway: "", pg: "", self_amount: false, suggested_amounts: "", max_attempts: "", max_successful_payments: "1", wallet_target: "partner", auto_credit: true });
      await loadLinks();
      // Auto-copy URL to clipboard
      await copyToClipboard(payUrl(data.link), { successMessage: "URL copied", silent: false });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = formatApiError(detail) || "Failed to create link";
      // Phase 7.22 — surface ManaRecharge account-side limit clearly so admin can act on it.
      if (status === 502 && /50,?000/i.test(String(msg))) {
        setPgBlocked(true);
        toast.error("ManaRecharge gateway is currently rejecting all transactions (account-side limit). Check your provider dashboard.", { duration: 8000 });
      } else {
        toast.error(msg);
      }
    } finally { setCreating(false); }
  };

  const refreshOne = async (link) => {
    setRefreshingId(link.id);
    try {
      const { data } = await api.post(`/admin/payment-links/${link.id}/refresh-status`);
      // Surface what ManaRecharge said — both endpoints, side by side.
      const pgr = data.pg_responses || {};
      const v1 = pgr.pgtxns_status?.message || pgr.pgtxns_status?.data?.remarks;
      const basic = pgr.transactionstatus?.message || pgr.transactionstatus?.data?.remarks;
      const detail =
        v1 || basic
          ? `  ·  v1: ${v1 || "—"}  ·  basic: ${basic || "—"}`
          : "";
      toast.success(`Status: ${data.status}${detail}`, { duration: 6000 });
      await loadLinks();
      await loadMasterBalance();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Refresh failed");
    } finally { setRefreshingId(null); }
  };

  const submitRevoke = async () => {
    if (!revokeOpen) return;
    try {
      await api.post(`/admin/payment-links/${revokeOpen.id}/revoke`, { reason: revokeReason || null });
      toast.success("Link revoked");
      setRevokeOpen(null);
      setRevokeReason("");
      await loadLinks();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Revoke failed");
    }
  };

  const pauseLink = async (linkId) => {
    try { await api.post(`/admin/payment-links/${linkId}/pause`); toast.success("Link paused"); await loadLinks(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Pause failed"); }
  };

  const resumeLink = async (linkId) => {
    try { await api.post(`/admin/payment-links/${linkId}/resume`); toast.success("Link resumed"); await loadLinks(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Resume failed"); }
  };

  const deactivateLink = async (linkId) => {
    if (!window.confirm("Permanently deactivate this link? This cannot be undone.")) return;
    try { await api.post(`/admin/payment-links/${linkId}/deactivate`); toast.success("Link deactivated permanently"); await loadLinks(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Deactivate failed"); }
  };


  const copyAndOpen = async (link) => {
    const url = payUrl(link);
    await copyToClipboard(url, { successMessage: "URL copied" });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyOnly = async (link) => {
    const url = payUrl(link);
    await copyToClipboard(url, { successMessage: "URL copied to clipboard" });
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Admin</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Payment Links</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Create hosted ManaRecharge checkout URLs to collect money. Money settles into the
            platform master wallet (or directly to a partner's wallet for top-up links).
          </p>
        </div>
        <div className="flex items-center gap-3">
          {masterBalance != null && (
            <div className="surface-card px-4 py-3" data-testid="master-wallet-card">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Wallet className="h-3 w-3" /> Master wallet
              </div>
              <div className="font-mono font-bold text-lg mt-0.5">{formatINR(masterBalance)}</div>
            </div>
          )}
          <Button onClick={() => setCreateOpen(true)} data-testid="create-payment-link-btn">
            <Plus className="h-4 w-4 mr-1" /> Create payment link
          </Button>
        </div>
      </div>

      {/* PG-blocked banner — Phase 7.22 */}
      {pgBlocked && (
        <div className="mt-5 rounded-md border border-warning/40 bg-warning/5 p-4 text-sm" data-testid="pg-blocked-banner">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-medium">ManaRecharge gateway is rejecting all new transactions</div>
              <div className="text-xs text-muted-foreground mt-1">
                The provider's safety / daily limit is hit (returns "Please do Transaction below Rs.50,000" for every amount, even small ones).
                Existing pending links still work. Open your ManaRecharge dashboard, check the active PG channel limits and daily/cumulative caps, then retry.
              </div>
              <Button size="sm" variant="outline" className="mt-3" onClick={() => setPgBlocked(false)}>Dismiss</Button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Summary */}
      <div className="mt-6">
        <PaymentSummary />
      </div>

      {/* Status counts */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
        {["pending", "paid", "inactive", "deactivated", "expired", "revoked", "failed"].map((s) => {
          const cfg = STATUS_PILL[s];
          const Icon = cfg.icon;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilterStatus(filterStatus === s ? "all" : s)}
              className={`surface-card p-3 text-left transition-all ${filterStatus === s ? "ring-2 ring-primary" : "hover:bg-accent/40"}`}
              data-testid={`status-card-${s}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Icon className="h-3 w-3" /> {s}
              </div>
              <div className="font-mono font-bold text-2xl mt-1">{totals[s] || 0}</div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <Label className="text-xs">Type</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-44 h-9" data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="partner_topup">Partner top-up</SelectItem>
              <SelectItem value="wallet_topup">Wallet top-up</SelectItem>
              <SelectItem value="generic">Generic</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-44 h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="inactive">Inactive (paused)</SelectItem>
              <SelectItem value="deactivated">Deactivated</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1 flex-1 min-w-64">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by UTR / provider ref, partner, mobile, link id, remarks…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9"
            data-testid="search-input"
          />
        </div>
        <Button variant="outline" size="sm" onClick={loadLinks} disabled={loading} data-testid="refresh-list">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {/* Table */}
      <div className="mt-6 surface-card overflow-x-auto" data-testid="links-table">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-accent/30">
            <tr>
              <th className="p-3">Created</th>
              <th className="p-3">Type</th>
              <th className="p-3">Recipient</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Status</th>
              <th className="p-3">Expiry</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLinks.length === 0 && !loading && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                <LinkIcon className="h-6 w-6 mx-auto opacity-40 mb-2" />
                No payment links yet. Create one to get started.
              </td></tr>
            )}
            {filteredLinks.map((l) => {
              const cfg = STATUS_PILL[l.status] || STATUS_PILL.pending;
              const Icon = cfg.icon;
              const isPaid = l.status === "paid";
              const isPending = l.status === "pending";
              return (
                <tr key={l.id} className="border-t border-border hover:bg-accent/20 cursor-pointer" onClick={() => navigate(`/admin/payment-links/${l.id}`)} data-testid={`link-row-${l.id}`}>
                  <td className="p-3 text-xs font-mono text-muted-foreground">{fmtIST(l.created_at)}</td>
                  <td className="p-3">
                    {l.type === "partner_topup"
                      ? <Badge variant="outline" className="border-primary/40 text-primary text-[10px]">Partner top-up</Badge>
                      : <Badge variant="outline" className="text-[10px]">Generic</Badge>}
                  </td>
                  <td className="p-3 text-xs">
                    {l.partner_snapshot ? (
                      <>
                        <div className="font-medium">{l.partner_snapshot.full_name}</div>
                        <div className="text-muted-foreground font-mono">{l.partner_snapshot.mobile}</div>
                      </>
                    ) : (
                      <span className="text-muted-foreground italic">{l.remarks || "Generic collection"}</span>
                    )}
                  </td>
                  <td className="p-3 font-mono font-semibold">{formatINR(l.amount)}</td>
                  <td className="p-3">
                    <Badge variant="outline" className={`${cfg.color} text-[10px] gap-1`}>
                      <Icon className="h-3 w-3" /> {l.status}
                    </Badge>
                    {isPaid && l.paid_at && (
                      <div className="text-[10px] text-muted-foreground mt-1 font-mono">{fmtIST(l.paid_at)}</div>
                    )}
                    {isPaid && l.type === "wallet_topup" && l.credit_status && l.credit_status !== "credited" && (
                      <div className="mt-1" data-testid={`credit-status-${l.id}`}>
                        <Badge
                          variant="outline"
                          className={
                            l.credit_status === "pending_paymode_approval"
                              ? "bg-amber-50 text-amber-700 border-amber-300 text-[10px]"
                              : l.credit_status === "paymode_rejected"
                                ? "bg-red-50 text-red-700 border-red-300 text-[10px]"
                                : "bg-slate-50 text-slate-700 border-slate-300 text-[10px]"
                          }
                        >
                          {l.credit_status === "pending_paymode_approval" ? "Awaiting paymode approval"
                            : l.credit_status === "paymode_rejected" ? "Paymode rejected"
                            : l.credit_status}
                        </Badge>
                      </div>
                    )}
                    {isPaid && l.type === "wallet_topup" && l.wallet_credited && (
                      <div className="mt-1">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 text-[10px]">
                          Wallet credited
                        </Badge>
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground font-mono">{fmtIST(l.expires_at)}</td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => copyAndOpen(l)}
                        title="Copy + open in new tab"
                        data-testid={`copy-open-${l.id}`}
                      >
                        <Copy className="h-3.5 w-3.5 mr-0.5" /><ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => copyOnly(l)}
                        title="Copy URL only"
                        data-testid={`copy-only-${l.id}`}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <QrButton
                        url={payUrl(l)}
                        filename={`payment-link-${l.id?.slice(0, 8)}.png`}
                        title="Show QR code"
                        testId={`qr-btn-${l.id}`}
                      />
                      {isPending && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => refreshOne(l)}
                            disabled={refreshingId === l.id} title="Refresh status" data-testid={`refresh-${l.id}`}>
                            {refreshingId === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => pauseLink(l.id)}
                            title="Pause (inactive)" data-testid={`pause-${l.id}`} className="text-orange-600 hover:bg-orange-50">
                            <PauseCircle className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setRevokeOpen(l)}
                            className="text-destructive hover:bg-destructive/10" title="Revoke" data-testid={`revoke-${l.id}`}>
                            <Ban className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deactivateLink(l.id)}
                            className="text-gray-500 hover:bg-gray-100" title="Deactivate permanently" data-testid={`deactivate-${l.id}`}>
                            <Power className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                      {l.status === "inactive" && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => resumeLink(l.id)}
                            title="Resume" data-testid={`resume-${l.id}`} className="text-emerald-600 hover:bg-emerald-50">
                            <PlayCircle className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deactivateLink(l.id)}
                            className="text-gray-500 hover:bg-gray-100" title="Deactivate permanently" data-testid={`deactivate-${l.id}`}>
                            <Power className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-dialog">
          <DialogHeader>
            <DialogTitle>Create payment link</DialogTitle>
            <DialogDescription>
              Hosted on ManaRecharge. Settles into the master wallet (generic) or directly into the
              chosen partner's wallet (partner top-up) on success.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Link type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, partner_id: "", customer_id: "" })}>
                <SelectTrigger className="mt-1" data-testid="form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="generic">Generic — money to master wallet</SelectItem>
                  <SelectItem value="partner_topup">Partner top-up — money to partner's wallet</SelectItem>
                  <SelectItem value="wallet_topup">Wallet top-up — add balance via payment link</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment gateway <span className="text-muted-foreground text-[10px]">optional — defaults to ManaRecharge</span></Label>
              <Select value={form.gateway || "manarecharge"} onValueChange={(v) => { setForm({ ...form, gateway: v, pg: "" }); if (v === "manarecharge") loadPgChannels(); }}>
                <SelectTrigger className="mt-1" data-testid="form-gateway"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {gateways.length === 0 && <SelectItem value="manarecharge">ManaRecharge (default)</SelectItem>}
                  {gateways.map((g) => (
                    <SelectItem key={g.id} value={g.id} disabled={!g.enabled}>
                      {g.label}{!g.enabled ? " (not configured)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(form.gateway === "manarecharge" || !form.gateway) && (
              <div>
                <Label className="flex items-center gap-2">
                  PG channel <span className="text-muted-foreground text-[10px]">optional — auto-selects if empty</span>
                  {pgRecommended?.recommended && (
                    <span
                      className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200"
                      title={`Best success in last 7 days: ${pgRecommended.paid}/${pgRecommended.total} paid (${pgRecommended.rate}%)`}
                      data-testid="pg-recommended-pill"
                    >
                      ✨ Smart: {pgRecommended.recommended} · {pgRecommended.rate}%
                    </span>
                  )}
                </Label>
                <Select value={form.pg || "_auto"} onValueChange={(v) => setForm({ ...form, pg: v === "_auto" ? "" : v })}>
                  <SelectTrigger className="mt-1" data-testid="form-pg-channel">
                    <SelectValue placeholder={pgChannelsLoading ? "Loading channels..." : "Auto (provider default)"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_auto">Auto (provider default)</SelectItem>
                    {pgChannels.map((ch) => {
                      const isRec = pgRecommended?.recommended === ch.id;
                      return (
                        <SelectItem key={ch.id} value={ch.id} disabled={!ch.active}>
                          {ch.name}{ch.active ? "" : " (inactive)"}{isRec ? " · ✨ best 7d" : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {pgRecommended?.recommended && form.pg === pgRecommended.recommended && (
                  <p className="text-[11px] text-emerald-700 mt-1">
                    Auto-selected — highest success rate in the last 7 days ({pgRecommended.paid}/{pgRecommended.total} paid).
                  </p>
                )}
              </div>
            )}
            {form.type === "partner_topup" && (
              <div>
                <Label>Partner</Label>
                <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="form-partner"><SelectValue placeholder="Pick a partner" /></SelectTrigger>
                  <SelectContent>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.full_name} · {p.mobile} · {p.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.type === "wallet_topup" && (
              <>
                <div>
                  <Label>Wallet target</Label>
                  <Select value={form.wallet_target} onValueChange={(v) => setForm({ ...form, wallet_target: v, partner_id: "", customer_id: "" })}>
                    <SelectTrigger className="mt-1" data-testid="form-wallet-target"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="partner">Partner wallet</SelectItem>
                      <SelectItem value="customer">Customer wallet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.wallet_target === "partner" && (
                  <div>
                    <Label>Partner</Label>
                    <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
                      <SelectTrigger className="mt-1" data-testid="form-wallet-partner"><SelectValue placeholder="Pick a partner" /></SelectTrigger>
                      <SelectContent>
                        {partners.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.full_name} · {p.mobile}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.wallet_target === "customer" && (
                  <div>
                    <Label>Customer ID</Label>
                    <Input value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                      placeholder="Enter customer ID" className="mt-1 font-mono" data-testid="form-wallet-customer" />
                  </div>
                )}
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <Label className="text-sm">Auto-credit on payment</Label>
                    <p className="text-[11px] text-muted-foreground">{form.auto_credit ? "Wallet credited immediately after payment (admin privilege)" : "Requires approval before wallet credit"}</p>
                  </div>
                  <Switch checked={form.auto_credit} onCheckedChange={(v) => setForm({ ...form, auto_credit: v })} data-testid="form-auto-credit" />
                </div>
              </>
            )}

            {/* Self-amount toggle */}
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label className="text-sm">Self-amount link</Label>
                <p className="text-[11px] text-muted-foreground">Let the payer choose their own amount</p>
              </div>
              <Switch
                checked={form.self_amount}
                onCheckedChange={(v) => setForm({ ...form, self_amount: v })}
                data-testid="form-self-amount"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{form.self_amount ? "Default amount (₹)" : "Amount (₹) *"}</Label>
                <Input type="number" min={form.self_amount ? "0" : "1"} value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} className="font-mono mt-1"
                  data-testid="form-amount" placeholder={form.self_amount ? "0 (user enters)" : "500"} />
              </div>
              <div>
                <Label>Expires in (hours)</Label>
                <Input type="number" min="1" max="720" value={form.expires_in_hours}
                  onChange={(e) => setForm({ ...form, expires_in_hours: e.target.value })} className="font-mono mt-1" data-testid="form-expiry" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Min amount (₹) <span className="text-muted-foreground text-[10px]">optional</span></Label>
                <Input type="number" min="0" value={form.min_amount}
                  onChange={(e) => setForm({ ...form, min_amount: e.target.value })} className="font-mono mt-1" data-testid="form-min" placeholder="—" />
              </div>
              <div>
                <Label>Max amount (₹) <span className="text-muted-foreground text-[10px]">optional</span></Label>
                <Input type="number" min="0" value={form.max_amount}
                  onChange={(e) => setForm({ ...form, max_amount: e.target.value })} className="font-mono mt-1" data-testid="form-max" placeholder="—" />
              </div>
            </div>
            {form.self_amount && (
              <div>
                <Label>Suggested amounts <span className="text-muted-foreground text-[10px]">comma-separated</span></Label>
                <Input value={form.suggested_amounts} onChange={(e) => setForm({ ...form, suggested_amounts: e.target.value })}
                  className="font-mono mt-1" data-testid="form-suggested" placeholder="500, 1000, 2000, 5000" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Max attempts <span className="text-muted-foreground text-[10px]">optional</span></Label>
                <Input type="number" min="1" value={form.max_attempts}
                  onChange={(e) => setForm({ ...form, max_attempts: e.target.value })} className="font-mono mt-1" data-testid="form-max-attempts" placeholder="Unlimited" />
              </div>
              <div>
                <Label>Max successful payments</Label>
                <Input type="number" min="1" value={form.max_successful_payments}
                  onChange={(e) => setForm({ ...form, max_successful_payments: e.target.value })} className="font-mono mt-1" data-testid="form-max-success" />
              </div>
            </div>
            <div>
              <Label>Remarks <span className="text-muted-foreground text-[10px]">optional</span></Label>
              <Textarea
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                className="mt-1"
                rows={2}
                data-testid="form-remarks"
                placeholder="Internal note · shown in admin only"
              />
            </div>
          </div>

          {/* URL preview — shows exactly what URL will be generated. Surfaces
              domain-misconfig issues BEFORE the admin clicks Create. */}
          <div className="rounded-xl border border-blue-200 bg-blue-50/60 px-4 py-3 mt-2" data-testid="url-preview">
            <div className="flex items-start gap-2.5">
              <LinkIcon className="h-4 w-4 mt-0.5 text-blue-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold tracking-[0.12em] uppercase text-blue-700 mb-1">
                  Payer will land on
                </p>
                <code className="text-xs font-mono text-blue-900 break-all">
                  {buildPreviewUrl(partnerDomain)}
                </code>
                {!partnerDomain && (
                  <p className="text-[11px] text-amber-700 mt-1.5 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    No partner domain set — using current origin. Configure it in Admin → Domains to lock links to your customer-facing domain.
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate} disabled={creating} data-testid="form-submit">
              {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Create link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke dialog */}
      <Dialog open={!!revokeOpen} onOpenChange={(o) => { if (!o) { setRevokeOpen(null); setRevokeReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="revoke-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" /> Revoke payment link
            </DialogTitle>
            <DialogDescription>
              Once revoked, anyone who opens this URL will see the gateway's "expired" page. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason <span className="text-muted-foreground text-[10px]">optional</span></Label>
            <Textarea
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              className="mt-1"
              rows={2}
              data-testid="revoke-reason"
              placeholder="e.g. Wrong amount · created by mistake"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeOpen(null)}>Cancel</Button>
            <Button variant="destructive" onClick={submitRevoke} data-testid="revoke-confirm">
              <Ban className="h-4 w-4 mr-1" /> Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
